/**
 * Pure span algebra for Inline Formatting (F-049 / UAT-21) — coalesce, clamp,
 * normalize, and toggle. Zero-infra (Constitution §III); consumed by the
 * `apps/api` validation boundary AND the `ui-suvoda` editor view, so the
 * risk-bearing logic lives here once and is exhaustively unit-tested.
 *
 * Offsets are 0-based Unicode code points, half-open `[offset, offset + length)`.
 */
import type { FormattingMark, FormattingMarkKind, Span } from './types.js';
import { FORMATTING_MARK_KINDS } from './types.js';

/** Exclusive end (code points) of a span. */
const end = (span: Span): number => span.offset + span.length;

/**
 * Clamp every span to `[0, bodyLen]` code points, dropping any that collapse to
 * zero length (FR-016 / I-RT-02/03). Generic over the span subtype so the extra
 * `kind` (marks) or `colour` (highlights) fields are preserved.
 */
export const clampToBody = <T extends Span>(spans: readonly T[], bodyLen: number): T[] => {
  const clamped: T[] = [];
  for (const span of spans) {
    const start = Math.max(0, Math.min(span.offset, bodyLen));
    const stop = Math.max(start, Math.min(end(span), bodyLen));
    if (stop - start >= 1) {
      clamped.push({ ...span, offset: start, length: stop - start });
    }
  }
  return clamped;
};

/**
 * Coalesce same-kind contiguous/overlapping marks into single spans (FR-006 /
 * I-RT-04). Different kinds are processed independently and never merged
 * (FR-005 / I-RT-05). Output is sorted by `(offset, kind)`, and per kind is
 * non-overlapping and gap-separated. Zero/negative-length marks are dropped.
 */
export const coalesce = (marks: readonly FormattingMark[]): FormattingMark[] => {
  const result: FormattingMark[] = [];
  for (const kind of FORMATTING_MARK_KINDS) {
    const ofKind = marks
      .filter((mark) => mark.kind === kind && mark.length >= 1)
      .sort((a, b) => a.offset - b.offset);
    let run: { offset: number; stop: number } | null = null;
    for (const mark of ofKind) {
      if (run === null) {
        run = { offset: mark.offset, stop: end(mark) };
      } else if (mark.offset <= run.stop) {
        run.stop = Math.max(run.stop, end(mark));
      } else {
        result.push({ offset: run.offset, length: run.stop - run.offset, kind });
        run = { offset: mark.offset, stop: end(mark) };
      }
    }
    if (run !== null) {
      result.push({ offset: run.offset, length: run.stop - run.offset, kind });
    }
  }
  return result.sort((a, b) => a.offset - b.offset || a.kind.localeCompare(b.kind));
};

/** Canonical form: clamp to the body bounds, then coalesce same-kind spans. */
export const normalize = (marks: readonly FormattingMark[], bodyLen: number): FormattingMark[] =>
  coalesce(clampToBody(marks, bodyLen));

/** Whether `[start, stop)` is fully covered by the (coalesced, sorted) spans. */
const isRangeCovered = (
  sorted: readonly FormattingMark[],
  start: number,
  stop: number,
): boolean => {
  let cursor = start;
  for (const span of sorted) {
    if (span.offset > cursor) {
      return false; // a gap before this span ⇒ not contiguous coverage
    }
    if (end(span) > cursor) {
      cursor = end(span);
    }
    if (cursor >= stop) {
      return true;
    }
  }
  return cursor >= stop;
};

/** The portions of `mark` left after removing `[start, stop)`. */
const subtractRange = (
  mark: FormattingMark,
  start: number,
  stop: number,
  kind: FormattingMarkKind,
): FormattingMark[] => {
  const pieces: FormattingMark[] = [];
  if (mark.offset < start) {
    pieces.push({ offset: mark.offset, length: Math.min(end(mark), start) - mark.offset, kind });
  }
  if (end(mark) > stop) {
    const right = Math.max(mark.offset, stop);
    pieces.push({ offset: right, length: end(mark) - right, kind });
  }
  return pieces;
};

/**
 * Toggle a single `kind` over `range` (Word/Docs semantics): if the whole range
 * is already covered by that kind, remove it there; otherwise add it across the
 * whole range. Other kinds are untouched (FR-001/002/005). Returns coalesced
 * marks; clamp to the body separately via {@link clampToBody}/{@link normalize}.
 */
export const toggleKind = (
  marks: readonly FormattingMark[],
  range: Span,
  kind: FormattingMarkKind,
): FormattingMark[] => {
  if (range.length < 1) {
    return coalesce(marks);
  }
  const start = range.offset;
  const stop = end(range);
  const sameKind = coalesce(marks.filter((mark) => mark.kind === kind));
  const others = marks.filter((mark) => mark.kind !== kind);

  const updated = isRangeCovered(sameKind, start, stop)
    ? sameKind.flatMap((mark) => subtractRange(mark, start, stop, kind))
    : coalesce([...sameKind, { offset: start, length: stop - start, kind }]);

  return coalesce([...others, ...updated]);
};
